import { Controller } from 'stimulus'
import $ from 'jquery'
import { CustomerDraft } from '~/site/pages/cart/customer_draft'
import { updateCartOnEmailChange } from '~/site/pages/cart/common'

// A Stimulus reconnect on the same form is not a new navigation or another reload.
const drafts = new WeakMap()
const initialURL = window.location.href
let reloadAllowed = true

export default class extends Controller {
  static values = { names: Array, key: String }

  connect() {
    const key = `cart-customer-draft:${this.keyValue}:${window.location.href}`
    this.state = drafts.get(this.element)
    if (!this.state || this.state.draft.key !== key) {
      let storage
      try {
        storage = window.sessionStorage
      } catch {
        /* Keep in-flight edits in memory. */
      }
      this.state = {
        draft: new CustomerDraft({
          form: this.element,
          names: this.namesValue,
          key,
          storage,
          reload:
            reloadAllowed &&
            window.location.href === initialURL &&
            performance.getEntriesByType('navigation')[0]?.type === 'reload',
        }),
        changedDuringRequest: new Set(),
        restored: false,
      }
      drafts.set(this.element, this.state)
    }
    this.draft = this.state.draft
    this.element.addEventListener(
      'cart-customer-draft:request-started',
      this.requestStarted
    )
    this.element.addEventListener(
      'cart-customer-draft:first-entrant-revealed',
      this.firstEntrantRevealed
    )
    // Capture before field-level change handlers take their request's FormData snapshot.
    this.element.addEventListener('input', this.capture, true)
    this.element.addEventListener('change', this.capture, true)
    document.addEventListener('turbo:before-visit', this.leave)
    window.addEventListener('pageshow', this.pageShown)
    $(document).on('simplero-cart-updated', this.restore)
    if (!this.state.restored) {
      this.state.restored = true
      if (this.draft.restore()) {
        // If another request is already running, it has not seen these restored values.
        // Keep them dirty until request-started confirms an actual FormData snapshot.
        for (const name of Object.keys(this.draft.values))
          this.state.changedDuringRequest.add(name)
        updateCartOnEmailChange()
      }
    }
  }

  disconnect() {
    this.element.removeEventListener(
      'cart-customer-draft:request-started',
      this.requestStarted
    )
    this.element.removeEventListener(
      'cart-customer-draft:first-entrant-revealed',
      this.firstEntrantRevealed
    )
    this.element.removeEventListener('input', this.capture, true)
    this.element.removeEventListener('change', this.capture, true)
    document.removeEventListener('turbo:before-visit', this.leave)
    window.removeEventListener('pageshow', this.pageShown)
    $(document).off('simplero-cart-updated', this.restore)
  }

  firstEntrantRevealed = () => {
    // Preserve the explicit reveal even before the buyer edits a participant.
    this.draft.firstEntrantRevealed = true
    this.draft.at = this.draft.now()
    this.draft.save()
  }

  capture = (event) => {
    if (event.isTrusted === false || !this.draft?.eligible(event.target)) return
    for (const name of this.draft.capture(event.target))
      this.state.changedDuringRequest.add(name)
  }

  requestStarted = (event) => {
    this.state.changedDuringRequest.clear()
    this.state.fullReconciliationInFlight =
      event?.detail?.path?.split('?')[0].endsWith('/update_on_email_change') ===
      true
  }

  restore = () => {
    // Only replay edits made after the request snapshot, not normalized values or removed fields.
    const restored = this.draft?.restore(this.state.changedDuringRequest, {
      reconciled: this.state.fullReconciliationInFlight,
    })
    this.state.changedDuringRequest.clear()
    if (restored) updateCartOnEmailChange()
  }

  pageShown = (event) => {
    if (event.persisted) this.leave()
  }

  leave = () => {
    // Navigation through Turbo keeps performance.navigation.type from the original document.
    reloadAllowed = false
    this.state.changedDuringRequest.clear()
    this.draft.pending.clear()
    this.draft.firstEntrantRevealed = false
    this.draft.values = {}
    this.draft.countries = {}
    try {
      this.draft.storage?.removeItem(this.draft.key)
    } catch {
      /* Storage is optional. */
    }
  }
}
